// Community Rooms - Room management and controls
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import {
    collection,
    getDocs,
    doc,
    updateDoc,
    addDoc,
    deleteDoc,
    onSnapshot,
    serverTimestamp
} from 'firebase/firestore';
import { DEFAULT_COMMUNITIES, seedDefaultChatRooms } from '../utils/seedDefaults';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Chip,
    Avatar,
    AvatarGroup,
    LinearProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PushPinIcon from '@mui/icons-material/PushPin';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';

const CommunityRooms = () => {
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialog, setCreateDialog] = useState(false);
    const [editDialog, setEditDialog] = useState({ open: false, room: null });
    const [pinDialog, setPinDialog] = useState({ open: false, room: null });
    const [newRoom, setNewRoom] = useState({ name: '', description: '', topic: '', isPalsRoom: false, communityId: 'ttc_warriors' });
    const [editForm, setEditForm] = useState({ name: '', description: '', topic: '', isPalsRoom: false, communityId: '' });
    const [communities, setCommunities] = useState(DEFAULT_COMMUNITIES);
    const [pinnedMessage, setPinnedMessage] = useState('');
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        let unsubscribe = () => {};
        let cancelled = false;

        const start = async () => {
            setLoading(true);
            try {
                await seedDefaultChatRooms(db);
                const communitySnap = await getDocs(collection(db, 'forum_communities'));
                if (!cancelled && !communitySnap.empty) {
                    setCommunities(communitySnap.docs.map((d) => ({ id: d.id, name: d.data().name || d.id })));
                }
            } catch (error) {
                console.warn('Could not seed rooms / load communities:', error);
            }
            if (cancelled) return;

            unsubscribe = onSnapshot(
                collection(db, 'chat_rooms'),
                (snapshot) => {
                    setRooms(snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id })));
                    setLoading(false);
                },
                (error) => {
                    console.error('Error loading rooms:', error);
                    setAlert({ severity: 'error', message: 'Failed to load rooms. Check your connection and try again.' });
                    setRooms([]);
                    setLoading(false);
                }
            );
        };

        start();
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    const handleCreateRoom = async () => {
        try {
            const communityId = newRoom.isPalsRoom
                ? 'pals_support'
                : (newRoom.communityId || 'ttc_warriors');
            await addDoc(collection(db, 'chat_rooms'), {
                name: newRoom.name.trim(),
                description: newRoom.description.trim(),
                topic: newRoom.topic.trim(),
                isPalsRoom: !!newRoom.isPalsRoom,
                communityId,
                emoji: newRoom.isPalsRoom ? '💜' : '💬',
                memberCount: 0,
                messageCount: 0,
                isLocked: false,
                createdAt: serverTimestamp(),
                lastMessageAt: serverTimestamp(),
            });
            setAlert({ severity: 'success', message: 'Room created successfully' });
            setCreateDialog(false);
            setNewRoom({ name: '', description: '', topic: '', isPalsRoom: false, communityId: 'ttc_warriors' });
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to create room' });
        }
    };

    const handleToggleLock = async (room) => {
        try {
            await updateDoc(doc(db, 'chat_rooms', room.id), {
                isLocked: !room.isLocked
            });
            setAlert({
                severity: 'success',
                message: `Room ${room.isLocked ? 'unlocked' : 'locked'}`
            });
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to update room' });
        }
    };

    const handlePinMessage = async () => {
        if (!pinDialog.room || !pinnedMessage.trim()) return;

        try {
            await updateDoc(doc(db, 'chat_rooms', pinDialog.room.id), {
                pinnedMessage: pinnedMessage.trim(),
                pinnedAt: serverTimestamp()
            });
            setAlert({ severity: 'success', message: 'Message pinned' });
            setPinDialog({ open: false, room: null });
            setPinnedMessage('');
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to pin message' });
        }
    };

    const handleEditRoom = async () => {
        if (!editDialog.room) return;
        try {
            await updateDoc(doc(db, 'chat_rooms', editDialog.room.id), {
                name: editForm.name.trim() || editDialog.room.name,
                description: editForm.description.trim(),
                topic: editForm.topic.trim(),
                isPalsRoom: editForm.isPalsRoom,
                communityId: editForm.isPalsRoom ? 'pals_support' : (editForm.communityId || editDialog.room.communityId || 'ttc_warriors'),
            });
            setAlert({ severity: 'success', message: 'Room updated' });
            setEditDialog({ open: false, room: null });
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to update room' });
        }
    };

    const handleDeleteRoom = async (room) => {
        if (!window.confirm(`Delete room "${room.name}"? This cannot be undone.`)) return;

        try {
            await deleteDoc(doc(db, 'chat_rooms', room.id));
            setAlert({ severity: 'success', message: 'Room deleted' });
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to delete room' });
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Community Rooms
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage chat rooms and community spaces
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialog(true)}
                    sx={{ bgcolor: '#FF6B9D' }}
                >
                    Create Room
                </Button>
            </Box>

            {/* Alert */}
            {alert && (
                <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {/* Rooms Grid */}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {!loading && rooms.length === 0 && (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 8 }}>
                        <ChatIcon sx={{ fontSize: 48, mb: 1, opacity: 0.35 }} />
                        <Typography variant="h6" gutterBottom>No rooms yet</Typography>
                        <Typography color="text.secondary">
                            Create a room to start monitoring community conversations.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Grid container spacing={3}>
                {rooms.map((room) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={room.id}>
                        <Card sx={{
                            height: '100%',
                            borderLeft: room.isPalsRoom ? '4px solid #96CEB4' : 'none',
                            opacity: room.isLocked ? 0.7 : 1
                        }}>
                            <CardContent>
                                {/* Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold">
                                            {room.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                            {room.topic && (
                                                <Chip size="small" label={room.topic} variant="outlined" />
                                            )}
                                            {room.isPalsRoom && (
                                                <Chip size="small" label="PALS" color="success" />
                                            )}
                                            {room.isLocked && (
                                                <Chip size="small" label="Locked" icon={<LockIcon />} color="warning" />
                                            )}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleToggleLock(room)}
                                        color={room.isLocked ? 'warning' : 'default'}
                                    >
                                        {room.isLocked ? <LockIcon /> : <LockOpenIcon />}
                                    </IconButton>
                                </Box>

                                {/* Description */}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                                    {room.description || 'No description'}
                                </Typography>

                                {/* Stats */}
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PeopleIcon fontSize="small" color="action" />
                                        <Typography variant="body2">{room.memberCount || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ChatIcon fontSize="small" color="action" />
                                        <Typography variant="body2">{room.messageCount || 0}</Typography>
                                    </Box>
                                </Box>

                                {/* Pinned Message */}
                                {room.pinnedMessage && (
                                    <Box sx={{
                                        bgcolor: '#FFF5E6',
                                        p: 1,
                                        borderRadius: 1,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1
                                    }}>
                                        <PushPinIcon fontSize="small" sx={{ color: '#FF9800' }} />
                                        <Typography variant="caption">{room.pinnedMessage}</Typography>
                                    </Box>
                                )}

                                {/* Actions */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        startIcon={<PushPinIcon />}
                                        onClick={() => { setPinDialog({ open: true, room }); setPinnedMessage(room.pinnedMessage || ''); }}
                                    >
                                        Pin
                                    </Button>
                                    <Button
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => {
                                            setEditForm({
                                                name: room.name || '',
                                                description: room.description || '',
                                                topic: room.topic || '',
                                                isPalsRoom: !!room.isPalsRoom,
                                                communityId: room.communityId || 'ttc_warriors',
                                            });
                                            setEditDialog({ open: true, room });
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteRoom(room)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Create Room Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Room</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Room Name"
                        value={newRoom.name}
                        onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={newRoom.description}
                        onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={2}
                    />
                    <TextField
                        fullWidth
                        label="Topic"
                        value={newRoom.topic}
                        onChange={(e) => setNewRoom({ ...newRoom, topic: e.target.value })}
                        margin="normal"
                        placeholder="e.g., TTC, Pregnancy, PALS"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Community</InputLabel>
                        <Select
                            value={newRoom.communityId}
                            label="Community"
                            onChange={(e) => setNewRoom({ ...newRoom, communityId: e.target.value })}
                            disabled={newRoom.isPalsRoom}
                        >
                            {communities.map((community) => (
                                <MenuItem key={community.id} value={community.id}>
                                    {community.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        sx={{
                            mt: 2,
                            ml: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                            '& .MuiCheckbox-root': { p: 0.5 },
                            '& .MuiFormControlLabel-label': { ml: 0 },
                        }}
                        control={
                            <Checkbox
                                checked={newRoom.isPalsRoom}
                                onChange={(e) => setNewRoom({ ...newRoom, isPalsRoom: e.target.checked })}
                            />
                        }
                        label="PALS Support Room (Pregnancy & Infant Loss)"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateRoom} disabled={!newRoom.name.trim()}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Room Dialog */}
            <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, room: null })} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Room — {editDialog.room?.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Room Name"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={2}
                    />
                    <TextField
                        fullWidth
                        label="Topic"
                        value={editForm.topic}
                        onChange={(e) => setEditForm({ ...editForm, topic: e.target.value })}
                        margin="normal"
                        placeholder="e.g., TTC, Pregnancy, PALS"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Community</InputLabel>
                        <Select
                            value={editForm.communityId || ''}
                            label="Community"
                            onChange={(e) => setEditForm({ ...editForm, communityId: e.target.value })}
                            disabled={editForm.isPalsRoom}
                        >
                            {communities.map((community) => (
                                <MenuItem key={community.id} value={community.id}>
                                    {community.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        sx={{
                            mt: 2,
                            ml: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                            '& .MuiCheckbox-root': { p: 0.5 },
                            '& .MuiFormControlLabel-label': { ml: 0 },
                        }}
                        control={
                            <Checkbox
                                checked={editForm.isPalsRoom}
                                onChange={(e) => setEditForm({ ...editForm, isPalsRoom: e.target.checked })}
                            />
                        }
                        label="PALS Support Room (Pregnancy & Infant Loss)"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setEditDialog({ open: false, room: null })}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditRoom}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Pin Message Dialog */}
            <Dialog open={pinDialog.open} onClose={() => setPinDialog({ open: false, room: null })} maxWidth="sm" fullWidth>
                <DialogTitle>Pin Message to {pinDialog.room?.name}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Pinned messages appear at the top of the room. Useful for crisis helpline info.
                    </Alert>
                    <TextField
                        fullWidth
                        label="Pinned Message"
                        value={pinnedMessage}
                        onChange={(e) => setPinnedMessage(e.target.value)}
                        multiline
                        rows={3}
                        placeholder="e.g., Crisis Helpline: 988 (Suicide & Crisis Lifeline)"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setPinDialog({ open: false, room: null })}>Cancel</Button>
                    <Button variant="contained" onClick={handlePinMessage}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CommunityRooms;
